// API bindings: events 

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { EventData, EventTriggerData, EventCountResponse, BroadcastEventData, BroadcastEventResponse, CreateEventForPatientResponse, GetPatientEventsResponse, GetDoctorPatientsEventsResponse, GetScheduledEventsResponse, UpdateEventData, UpdateEventResponse, DeleteEventResponse, GeneralEventData, GeneralEventResponse, CreateGeneralEventErrorHandler, AlarmScheduleData, TimerCreateData } from "./definitions";

export class ApiEvents {
    /**
     * Method: GET
     * Path: /events
     * Get events for the authenticated user
     * @param skip Number of events to skip (optional)
     * @param limit Maximum number of events to return (optional, max 100)
     * @param lang locale user selected
     * @param type Filter by event type (notification, alarm, timer)
     * @returns The request parameters
     */
    public static GetUserEvents(skip?: number, limit?: number, lang?: string, type?: string): RequestParams<EventData[], GetEventsErrorHandler> {
        const query = generateURIQuery({ skip, limit, lang, type });
        return {
            method: "GET",
            url: getApiUrl(`/events${query}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "INVALID_PAGINATION", handler.badRequest)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /events/unread
     * Get unread events for the authenticated user
     * @param skip Number of events to skip (optional)
     * @param limit Maximum number of events to return (optional, max 100)
     * @param lang Language preference (en/es, optional, defaults to en)
     * @param type Filter by event type (notification, alarm, timer)
     * @returns The request parameters
     */
    public static GetUserUnreadEvents(skip?: number, limit?: number, lang?: string, type?: string): RequestParams<EventData[], GetEventsErrorHandler> {
        const query = generateURIQuery({ skip, limit, lang, type });
        return {
            method: "GET",
            url: getApiUrl(`/events/unread${query}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "INVALID_PAGINATION", handler.badRequest)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /events/count
     * Get event count for the authenticated user
     * @param type Filter by event type (notification, alarm, timer)
     * @returns The request parameters
     */
    public static GetUserEventCount(type?: string): RequestParams<EventCountResponse, CommonAuthenticatedErrorHandler> {
        const query = type ? generateURIQuery({ type }) : "";
        return {
            method: "GET",
            url: getApiUrl(`/events/count${query}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /events/count/unread
     * Get unread event count for the authenticated user
     * @param type Filter by event type (notification, alarm, timer)
     * @returns The request parameters
     */
    public static GetUserUnreadEventCount(type?: string): RequestParams<EventCountResponse, CommonAuthenticatedErrorHandler> {
        const query = type ? generateURIQuery({ type }) : "";
        return {
            method: "GET",
            url: getApiUrl(`/events/count/unread${query}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: PUT
     * Path: /events/{eventId}/read
     * Mark an event as read
     * @param eventId Event ID
     * @returns The request parameters
     */
    public static MarkEventAsRead(eventId: string): RequestParams<void, MarkEventErrorHandler> {
        return {
            method: "PUT",
            url: getApiUrl(`/events/${eventId}/read`),
            json: {},
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(401, "*", handler.unauthorized)
                    .add(404, "EVENT_NOT_FOUND", handler.notFoundEventNotFound)
                    .add(404, "*", handler.notFound)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: PUT
     * Path: /events/markAllAsRead
     * Mark an event as read
     * @returns The request parameters
     */
    public static MarkAllEventsAsRead(): RequestParams<void, MarkEventErrorHandler> {
        return {
            method: "PUT",
            url: getApiUrl(`/events/markAllAsRead`),
            json: {},
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: PUT
     * Path: /events/{eventId}/unread
     * Mark an event as unread
     * @param eventId Event ID
     * @returns The request parameters
     */
    public static MarkEventAsUnread(eventId: string): RequestParams<void, MarkEventErrorHandler> {
        return {
            method: "PUT",
            url: getApiUrl(`/events/${eventId}/unread`),
            json: {},
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(401, "*", handler.unauthorized)
                    .add(404, "EVENT_NOT_FOUND", handler.notFoundEventNotFound)
                    .add(404, "*", handler.notFound)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: PUT
     * Path: /events/{eventId}/archive
     * Archive an event
     * @param eventId Event ID
     * @returns The request parameters
     */
    public static ArchiveEvent(eventId: string): RequestParams<void, MarkEventErrorHandler> {
        return {
            method: "PUT",
            url: getApiUrl(`/events/${eventId}/archive`),
            json: {},
            handleError: buildMarkEventErrorHandler,
        };
    }

    /**
     * Method: PUT
     * Path: /events/{eventId}/unarchive
     * Unarchive an event
     * @param eventId Event ID
     * @returns The request parameters
     */
    public static UnarchiveEvent(eventId: string): RequestParams<void, MarkEventErrorHandler> {
        return {
            method: "PUT",
            url: getApiUrl(`/events/${eventId}/unarchive`),
            json: {},
            handleError: buildMarkEventErrorHandler,
        };
    }

    /**
     * Method: GET
     * Path: /events/archived
     * Get archived events for the authenticated user
     * @param skip Number of events to skip (optional)
     * @param limit Maximum number of events to return (optional, max 100)
     * @param lang Language preference (en/es, optional, defaults to en)
     * @param type Filter by event type (notification, alarm, timer)
     * @returns The request parameters
     */
    public static GetUserArchivedEvents(skip?: number, limit?: number, lang?: string, type?: string): RequestParams<EventData[], GetEventsErrorHandler> {
        const query = generateURIQuery({ skip, limit, lang, type });
        return {
          method: "GET",
          url: getApiUrl(`/events/archived${query}`),
          handleError: (err, handler) => {
            new RequestErrorHandler()
                .add(400, "INVALID_PAGINATION", handler.badRequest)
                .add(400, "*", handler.badRequest)
                .add(401, "*", handler.unauthorized)
                .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                .handle(err);
        },
        };
    }
      
    /**
     * Method: GET
     * Path: /events/count/archived
     * Get archived event count for the authenticated user
     * @param type Filter by event type (notification, alarm, timer)
     * @returns The request parameters
     */
    public static GetUserArchivedEventCount(type?: string): RequestParams<EventCountResponse, CommonAuthenticatedErrorHandler> {
        const query = type ? generateURIQuery({ type }) : "";
        return {
          method: "GET",
          url: getApiUrl(`/events/count/archived${query}`),
          handleError: (err, handler) => {
            new RequestErrorHandler()
                .add(401, "*", handler.unauthorized)
                .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /events
     * Create a general event (supports all event types)
     * @param body General event data
     * @returns The request parameters
     */
    public static CreateGeneralEvent(body: GeneralEventData): RequestParams<GeneralEventResponse, CreateGeneralEventErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/events`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "MISSING_FIELDS", handler.badRequestMissingFields)
                    .add(400, "INVALID_EVENT_TYPE", handler.badRequestInvalidEventType)
                    .add(400, "*", handler.badRequest)
                    .add(401, "NOT_AUTHENTICATED", handler.unauthorizedNotAuthenticated)
                    .add(401, "INSUFFICIENT_PERMISSIONS", handler.unauthorizedInsufficientPermissions)
                    .add(401, "PATIENT_NOT_ASSIGNED", handler.unauthorizedPatientNotAssigned)
                    .add(401, "INVALID_TARGET", handler.unauthorizedInvalidTarget)
                    .add(401, "*", handler.unauthorized)
                    .add(404, "USER_NOT_FOUND", handler.notFoundUserNotFound)
                    .add(404, "*", handler.notFound)
                    .add(500, "CREATE_EVENT_ERROR", handler.serverErrorCreateEventError)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /events/trigger
     * Trigger an event (doctors and admins only)
     * @param body Event data
     * @returns The request parameters
     */
    public static TriggerEvent(body: EventTriggerData): RequestParams<EventData, TriggerEventErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/events/trigger`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "MISSING_FIELDS", handler.badRequestMissingFields)
                    .add(400, "INVALID_EVENT_TYPE", handler.badRequestInvalidEventType)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(404, "USER_NOT_FOUND", handler.notFoundUserNotFound)
                    .add(404, "*", handler.notFound)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /events/alarms/schedule
     * Schedule an alarm (doctors and admins only)
     * @param body Alarm data
     * @returns The request parameters
     */
    public static ScheduleAlarm(body: AlarmScheduleData): RequestParams<EventData, TriggerEventErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/events/alarms/schedule`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "MISSING_FIELDS", handler.badRequestMissingFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(404, "USER_NOT_FOUND", handler.notFoundUserNotFound)
                    .add(404, "*", handler.notFound)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /events/timers/create
     * Create a timer (doctors and admins only)
     * @param body Timer data
     * @returns The request parameters
     */
    public static CreateTimer(body: TimerCreateData): RequestParams<EventData, TriggerEventErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/events/timers/create`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "MISSING_FIELDS", handler.badRequestMissingFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(404, "USER_NOT_FOUND", handler.notFoundUserNotFound)
                    .add(404, "*", handler.notFound)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /events/broadcast/doctor
     * Create event for all patients of the authenticated doctor
     * @param body Broadcast event data
     * @returns The request parameters
     */
    public static BroadcastEventToPatients(body: BroadcastEventData): RequestParams<BroadcastEventResponse, BroadcastEventErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/events/broadcast/doctor`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "MISSING_FIELDS", handler.badRequestMissingFields)
                    .add(400, "INVALID_EVENT_TYPE", handler.badRequestInvalidEventType)
                    .add(400, "*", handler.badRequest)
                    .add(401, "NOT_AUTHENTICATED", handler.unauthorizedNotAuthenticated)
                    .add(401, "INSUFFICIENT_PERMISSIONS", handler.unauthorizedInsufficientPermissions)
                    .add(401, "*", handler.unauthorized)
                    .add(404, "NO_PATIENTS", handler.notFoundNoPatients)
                    .add(404, "*", handler.notFound)
                    .add(500, "BROADCAST_ERROR", handler.serverErrorBroadcastError)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /events/patient/{patientId}
     * Create event for specific patient
     * @param patientId Patient ID
     * @param body Event data
     * @returns The request parameters
     */
    public static CreateEventForPatient(patientId: string, body: BroadcastEventData): RequestParams<CreateEventForPatientResponse, CreateEventForPatientErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/events/patient/${patientId}`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "MISSING_FIELDS", handler.badRequestMissingFields)
                    .add(400, "INVALID_EVENT_TYPE", handler.badRequestInvalidEventType)
                    .add(400, "INVALID_USER_ROLE", handler.badRequestInvalidUserRole)
                    .add(400, "*", handler.badRequest)
                    .add(401, "NOT_AUTHENTICATED", handler.unauthorizedNotAuthenticated)
                    .add(401, "INSUFFICIENT_PERMISSIONS", handler.unauthorizedInsufficientPermissions)
                    .add(401, "PATIENT_NOT_ASSIGNED", handler.unauthorizedPatientNotAssigned)
                    .add(401, "*", handler.unauthorized)
                    .add(404, "PATIENT_NOT_FOUND", handler.notFoundPatientNotFound)
                    .add(404, "*", handler.notFound)
                    .add(500, "CREATE_EVENT_ERROR", handler.serverErrorCreateEventError)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /events/patient/{patientId}
     * Get events for specific patient (doctors and admins only)
     * @param patientId Patient ID
     * @param skip Number of events to skip (optional)
     * @param limit Maximum number of events to return (optional, max 100)
     * @param type Filter by event type (notification, alarm, timer) - optional
     * @param lang Language preference (en/es, optional, defaults to en)
     * @returns The request parameters
     */
    public static GetPatientEvents(patientId: string, skip?: number, limit?: number, type?: string, lang?: string): RequestParams<GetPatientEventsResponse, GetPatientEventsErrorHandler> {
        const query = generateURIQuery({ skip, limit, type, lang });
        return {
            method: "GET",
            url: getApiUrl(`/events/patient/${patientId}${query}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "INVALID_PAGINATION", handler.badRequestInvalidPagination)
                    .add(400, "INVALID_USER_ROLE", handler.badRequestInvalidUserRole)
                    .add(400, "*", handler.badRequest)
                    .add(401, "NOT_AUTHENTICATED", handler.unauthorizedNotAuthenticated)
                    .add(401, "INSUFFICIENT_PERMISSIONS", handler.unauthorizedInsufficientPermissions)
                    .add(401, "PATIENT_NOT_ASSIGNED", handler.unauthorizedPatientNotAssigned)
                    .add(401, "*", handler.unauthorized)
                    .add(404, "PATIENT_NOT_FOUND", handler.notFoundPatientNotFound)
                    .add(404, "*", handler.notFound)
                    .add(500, "RETRIEVE_EVENTS_ERROR", handler.serverErrorRetrieveEventsError)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /events/doctor/patients
     * Get all events for all patients of the authenticated doctor
     * @param skip Number of events to skip (optional)
     * @param limit Maximum number of events to return (optional, max 100)
     * @param type Filter by event type (notification, alarm, timer) - optional
     * @param lang Language preference (en/es, optional, defaults to en)
     * @param patientId Filter by specific patient ID (optional)
     * @returns The request parameters
     */
    public static GetDoctorPatientsEvents(skip?: number, limit?: number, type?: string, lang?: string, patientId?: string): RequestParams<GetDoctorPatientsEventsResponse, GetDoctorPatientsEventsErrorHandler> {
        const query = generateURIQuery({ skip, limit, type, lang, patientId });
        return {
            method: "GET",
            url: getApiUrl(`/events/doctor/patients${query}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "INVALID_PAGINATION", handler.badRequestInvalidPagination)
                    .add(400, "*", handler.badRequest)
                    .add(401, "NOT_AUTHENTICATED", handler.unauthorizedNotAuthenticated)
                    .add(401, "INSUFFICIENT_PERMISSIONS", handler.unauthorizedInsufficientPermissions)
                    .add(401, "PATIENT_NOT_ASSIGNED", handler.unauthorizedPatientNotAssigned)
                    .add(401, "*", handler.unauthorized)
                    .add(404, "NO_PATIENTS", handler.notFoundNoPatients)
                    .add(404, "*", handler.notFound)
                    .add(500, "RETRIEVE_EVENTS_ERROR", handler.serverErrorRetrieveEventsError)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /events/scheduled
     * Get scheduled events for authenticated user (used by TaskScheduler)
     * @returns The request parameters
     */
    public static GetScheduledEvents(): RequestParams<GetScheduledEventsResponse, GetEventsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl("/events/scheduled"),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /events/{eventId}/execute
     * Execute a scheduled event (publish to MQTT)
     * @param eventId The event ID to execute
     * @returns The request parameters
     */
    public static ExecuteScheduledEvent(eventId: string): RequestParams<void, ApiEventsErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/events/${eventId}/execute`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "NOT_SCHEDULED", handler.badRequest)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(404, "EVENT_NOT_FOUND", handler.badRequest)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: PUT
     * Path: /events/{eventId}
     * Update an existing event
     * @param eventId The event ID to update
     * @param body Event update data
     * @returns The request parameters
     */
    public static UpdateEvent(eventId: string, body: UpdateEventData): RequestParams<UpdateEventResponse, UpdateEventErrorHandler> {
        return {
            method: "PUT",
            url: getApiUrl(`/events/${eventId}`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "INVALID_EVENT_TYPE", handler.badRequestInvalidEventType)
                    .add(400, "*", handler.badRequest)
                    .add(401, "NOT_AUTHENTICATED", handler.unauthorizedNotAuthenticated)
                    .add(401, "INSUFFICIENT_PERMISSIONS", handler.unauthorizedInsufficientPermissions)
                    .add(401, "PATIENT_NOT_ASSIGNED", handler.unauthorizedPatientNotAssigned)
                    .add(401, "*", handler.unauthorized)
                    .add(404, "EVENT_NOT_FOUND", handler.notFoundEventNotFound)
                    .add(404, "PATIENT_NOT_FOUND", handler.notFoundPatientNotFound)
                    .add(500, "UPDATE_FAILED", handler.serverErrorUpdateFailed)
                    .add(500, "UPDATE_EVENT_ERROR", handler.serverErrorUpdateEventError)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /events/{eventId}
     * Delete an existing event
     * @param eventId The event ID to delete
     * @returns The request parameters
     */
    public static DeleteEvent(eventId: string): RequestParams<DeleteEventResponse, DeleteEventErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/events/${eventId}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(401, "NOT_AUTHENTICATED", handler.unauthorizedNotAuthenticated)
                    .add(401, "INSUFFICIENT_PERMISSIONS", handler.unauthorizedInsufficientPermissions)
                    .add(401, "PATIENT_NOT_ASSIGNED", handler.unauthorizedPatientNotAssigned)
                    .add(401, "*", handler.unauthorized)
                    .add(404, "EVENT_NOT_FOUND", handler.notFoundEventNotFound)
                    .add(404, "PATIENT_NOT_FOUND", handler.notFoundPatientNotFound)
                    .add(500, "DELETE_FAILED", handler.serverErrorDeleteFailed)
                    .add(500, "DELETE_EVENT_ERROR", handler.serverErrorDeleteEventError)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Base error handler for Events API
 */
export type ApiEventsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;
};

/**
 * Error handler for GetUserEvents, GetUserUnreadEvents, GetUserArchivedEvents
 */
export type GetEventsErrorHandler = ApiEventsErrorHandler;

/**
 * Error handler for MarkEventAsRead, MarkEventAsUnread, ArchiveEvent, UnarchiveEvent
 */
export type MarkEventErrorHandler = ApiEventsErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Event not found
     */
    notFoundEventNotFound: () => void;
};

/**
 * Error handler for TriggerEvent, ScheduleAlarm, CreateTimer
 */
export type TriggerEventErrorHandler = ApiEventsErrorHandler & {
    /**
     * Missing required fields
     */
    badRequestMissingFields: () => void;

    /**
     * Invalid event type
     */
    badRequestInvalidEventType: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Target user not found
     */
    notFoundUserNotFound: () => void;
};

/**
 * Error handler for BroadcastEventToPatients
 */
export type BroadcastEventErrorHandler = ApiEventsErrorHandler & {
    /**
     * Missing required fields
     */
    badRequestMissingFields: () => void;

    /**
     * Invalid event type
     */
    badRequestInvalidEventType: () => void;

    /**
     * User not authenticated
     */
    unauthorizedNotAuthenticated: () => void;

    /**
     * Insufficient permissions (not a doctor)
     */
    unauthorizedInsufficientPermissions: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Doctor has no assigned patients
     */
    notFoundNoPatients: () => void;

    /**
     * Server error during broadcast
     */
    serverErrorBroadcastError: () => void;
};

/**
 * Error handler for CreateEventForPatient
 */
export type CreateEventForPatientErrorHandler = ApiEventsErrorHandler & {
    /**
     * Missing required fields
     */
    badRequestMissingFields: () => void;

    /**
     * Invalid event type
     */
    badRequestInvalidEventType: () => void;

    /**
     * Target user is not a patient
     */
    badRequestInvalidUserRole: () => void;

    /**
     * User not authenticated
     */
    unauthorizedNotAuthenticated: () => void;

    /**
     * Insufficient permissions (not a doctor/admin)
     */
    unauthorizedInsufficientPermissions: () => void;

    /**
     * Doctor not assigned to this patient
     */
    unauthorizedPatientNotAssigned: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Patient not found
     */
    notFoundPatientNotFound: () => void;

    /**
     * Server error during event creation
     */
    serverErrorCreateEventError: () => void;
};

/**
 * Error handler for GetPatientEvents
 */
export type GetPatientEventsErrorHandler = ApiEventsErrorHandler & {
    /**
     * Invalid pagination parameters
     */
    badRequestInvalidPagination: () => void;

    /**
     * Target user is not a patient
     */
    badRequestInvalidUserRole: () => void;

    /**
     * User not authenticated
     */
    unauthorizedNotAuthenticated: () => void;

    /**
     * Insufficient permissions (not a doctor/admin)
     */
    unauthorizedInsufficientPermissions: () => void;

    /**
     * Doctor not assigned to this patient
     */
    unauthorizedPatientNotAssigned: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Patient not found
     */
    notFoundPatientNotFound: () => void;

    /**
     * Server error during events retrieval
     */
    serverErrorRetrieveEventsError: () => void;
};

/**
 * Error handler for GetDoctorPatientsEvents
 */
export type GetDoctorPatientsEventsErrorHandler = ApiEventsErrorHandler & {
    /**
     * Invalid pagination parameters
     */
    badRequestInvalidPagination: () => void;

    /**
     * User not authenticated
     */
    unauthorizedNotAuthenticated: () => void;

    /**
     * Insufficient permissions (not a doctor)
     */
    unauthorizedInsufficientPermissions: () => void;

    /**
     * Doctor not assigned to the specified patient (when using patientId filter)
     */
    unauthorizedPatientNotAssigned: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Doctor has no assigned patients
     */
    notFoundNoPatients: () => void;

    /**
     * Server error during events retrieval
     */
    serverErrorRetrieveEventsError: () => void;
};

/**
 * Error handler for UpdateEvent
 */
export type UpdateEventErrorHandler = ApiEventsErrorHandler & {
    /** Invalid event type */
    badRequestInvalidEventType: () => void;

    /** User not authenticated */
    unauthorizedNotAuthenticated: () => void;

    /** Insufficient permissions (not a doctor/admin) */
    unauthorizedInsufficientPermissions: () => void;

    /** Doctor not assigned to this patient */
    unauthorizedPatientNotAssigned: () => void;

    /** Event not found */
    notFoundEventNotFound: () => void;

    /** Patient not found */
    notFoundPatientNotFound: () => void;

    /** Update failed */
    serverErrorUpdateFailed: () => void;

    /** Update event error */
    serverErrorUpdateEventError: () => void;
};

/**
 * Error handler for DeleteEvent
 */
export type DeleteEventErrorHandler = ApiEventsErrorHandler & {
    /** User not authenticated */
    unauthorizedNotAuthenticated: () => void;

    /** Insufficient permissions (not a doctor/admin) */
    unauthorizedInsufficientPermissions: () => void;

    /** Doctor not assigned to this patient */
    unauthorizedPatientNotAssigned: () => void;

    /** Event not found */
    notFoundEventNotFound: () => void;

    /** Patient not found */
    notFoundPatientNotFound: () => void;

    /** Delete failed */
    serverErrorDeleteFailed: () => void;

    /** Delete event error */
    serverErrorDeleteEventError: () => void;
};


function buildMarkEventErrorHandler(err: any, handler: MarkEventErrorHandler) {
    new RequestErrorHandler()
      .add(401, "*", handler.unauthorized)
      .add(404, "EVENT_NOT_FOUND", handler.notFoundEventNotFound)
      .add(404, "*", handler.notFound)
      .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
      .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
      .handle(err);
}
